import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SEEN_KEY = "em_schedule2_education_seen_v1";

/**
 * One-time educational tooltip shown the first time a patient drafts a refill
 * for a Schedule II controlled substance. Sets a localStorage flag on dismiss
 * so it doesn't reappear. Trigger is `triggerOpen` — the parent flips it from
 * false → true once any eligibility row returns is_schedule_2:true.
 */
export default function Schedule2EducationDialog({ triggerOpen }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!triggerOpen) return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem(SEEN_KEY)) return;
    setOpen(true);
  }, [triggerOpen]);

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && dismiss()}>
      <AlertDialogContent data-testid="schedule2-education-dialog">
        <AlertDialogHeader>
          <div className="mx-auto w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-2">
            <ShieldCheck className="w-7 h-7 text-amber-700" />
          </div>
          <AlertDialogTitle className="text-center">About controlled medications</AlertDialogTitle>
          <AlertDialogDescription className="text-center leading-relaxed">
            We're required by federal DEA rules to verify your follow-up visit timing —
            that's why we ask before refilling controlled medications like Adderall, Vyvanse,
            Concerta, Ritalin, and Focalin. If your last visit was more than 30 days ago,
            you'll need to schedule a follow-up before your prescriber can authorize the refill.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={dismiss}
            className="w-full rounded-full"
            data-testid="schedule2-education-acknowledge"
          >
            Got it
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
